import { Box, ButtonBase, Tooltip, Typography } from '@mui/material'
import { FC, type ReactNode } from 'react'
import { useCreatePath, useTranslate } from 'react-admin'
import { Link } from 'react-router-dom'

import { type EventRow, type GanttBar, type GanttChart, ganttChart } from '../../data/source'
import { formatDateTime, formatDay } from '../../domain/dateTime'
import { EventMarkerIcon, PROGRESS_COLORS } from '../atoms/EventMarkerIcon'
import { useMarkerLabel } from './useMarkerLabel'

/** Label column, then the bars: the bars take whatever width is left, so nothing scrolls sideways. */
const COLUMNS = 'minmax(140px, 220px) minmax(0, 1fr)'
const BAR_HEIGHT = 18
const percent = (fraction: number): string => `${String(fraction * 100)}%`

/** The day lines and the now line, behind every row's bar. */
const Grid: FC<{ chart: GanttChart }> = ({ chart }) => (
  <>
    {chart.days.map(({ at, position }) => (
      <Box key={at} position="absolute" top={0} bottom={0} left={percent(position)} borderLeft={1} borderColor="divider" />
    ))}
    {chart.now !== null && (
      <Box position="absolute" top={0} bottom={0} left={percent(chart.now)} borderLeft={2} borderColor="error.light" />
    )}
  </>
)

/** The days along the top. */
const Axis: FC<{ chart: GanttChart }> = ({ chart }) => (
  <>
    <Box />
    <Box position="relative" height={20} data-testid="gantt-axis">
      {chart.days.map(({ at, position }) => (
        <Typography
          key={at}
          variant="caption"
          color="text.secondary"
          position="absolute"
          left={percent(position)}
          pl={0.5}
          whiteSpace="nowrap"
          data-testid="gantt-day"
        >
          {formatDay(at)}
        </Typography>
      ))}
    </Box>
  </>
)

const ROW_SX = {
  display: 'grid',
  gridTemplateColumns: COLUMNS,
  borderTop: 1,
  borderColor: 'divider',
  color: 'text.primary',
  textDecoration: 'none',
  textAlign: 'left',
  width: '100%',
} as const

/** A bar's fill: its progress color, fading out while it has no end. */
const barBackground = (bar: GanttBar): string => {
  const color = PROGRESS_COLORS[bar.marker.progress]
  return bar.open ? `linear-gradient(to right, ${color} 60%, transparent)` : color
}

/** Its name, then when it starts and ends: what hovering a bar says. */
const useBarTitle = () => {
  const markerLabel = useMarkerLabel()
  return ({ marker }: GanttBar): string =>
    `${markerLabel(marker)}: ${formatDateTime(marker.startTime)} – ${marker.endTime === null ? '…' : formatDateTime(marker.endTime)}`
}

/**
 * One event: its circle and name on the left, its bar on the shared axis on
 * the right; hovering the bar gives its start and end. An open event's bar
 * fades out where its end would be. A BOMBEO row opens the event's editor; a
 * stay's is read-only.
 */
const Row: FC<{ bar: GanttBar; chart: GanttChart }> = ({ bar, chart }) => {
  const createPath = useCreatePath()
  const markerLabel = useMarkerLabel()
  const { marker } = bar
  const label = markerLabel(marker)
  const stay = marker.type === 'PLACE'
  const barTitle = useBarTitle()
  const cells: ReactNode = (
    <>
      <Box display="flex" alignItems="center" gap={1} py={0.5} pr={1} minWidth={0}>
        <EventMarkerIcon marker={marker} label={label} size={20} />
        <Box minWidth={0}>
          {!stay && (
            <Typography variant="body2" fontWeight="bold" noWrap>
              {label}
            </Typography>
          )}
        </Box>
      </Box>
      <Box position="relative" alignSelf="stretch" display="flex" alignItems="center">
        <Grid chart={chart} />
        <Tooltip title={barTitle(bar)}>
          <Box
            data-testid={`gantt-bar-${marker.id}`}
            position="absolute"
            // Inline: a class per bar position would bloat the stylesheet.
            style={{ left: percent(bar.from), width: percent(bar.to - bar.from) }}
            minWidth={6}
            height={stay ? BAR_HEIGHT + 6 : BAR_HEIGHT}
            borderRadius={1}
            sx={{
              background: barBackground(bar),
              opacity: stay ? 0.35 : 1,
            }}
          />
        </Tooltip>
      </Box>
    </>
  )
  return stay ? (
    <Box sx={ROW_SX} data-testid={`timeline-card-${marker.id}`}>
      {cells}
    </Box>
  ) : (
    <ButtonBase
      component={Link}
      to={createPath({ resource: 'scale_events', type: 'edit', id: marker.id })}
      sx={{ ...ROW_SX, '&:hover': { bgcolor: 'action.hover' } }}
      data-testid={`timeline-card-${marker.id}`}
    >
      {cells}
    </ButtonBase>
  )
}

/**
 * Every MARPOL on one row: "MARPOL" on the left, and on the shared axis each
 * one's bar with its circle at its start. Each bar opens its own editor.
 */
const MarpolRow: FC<{ bars: GanttBar[]; chart: GanttChart }> = ({ bars, chart }) => {
  const translate = useTranslate()
  const createPath = useCreatePath()
  const markerLabel = useMarkerLabel()
  const barTitle = useBarTitle()
  return (
    <Box sx={ROW_SX} data-testid="timeline-row-marpol">
      <Box display="flex" alignItems="center" py={0.5} pr={1} minHeight={36}>
        <Typography variant="body2" fontWeight="bold" noWrap>
          {translate('enums.ScaleEventType.MARPOL')}
        </Typography>
      </Box>
      <Box position="relative" alignSelf="stretch">
        <Grid chart={chart} />
        {bars.map(bar => (
          <Tooltip key={bar.marker.id} title={barTitle(bar)}>
            <ButtonBase
              component={Link}
              to={createPath({ resource: 'scale_events', type: 'edit', id: bar.marker.id })}
              data-testid={`gantt-bar-${bar.marker.id}`}
              // Inline: a class per bar position would bloat the stylesheet.
              style={{ left: percent(bar.from), width: percent(bar.to - bar.from) }}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                minWidth: 24,
                justifyContent: 'flex-start',
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box position="absolute" left={0} right={0} height={BAR_HEIGHT} borderRadius={1} sx={{ background: barBackground(bar) }} />
              <Box position="relative">
                <EventMarkerIcon marker={bar.marker} label={markerLabel(bar.marker)} size={16} tooltip={false} />
              </Box>
            </ButtonBase>
          </Tooltip>
        ))}
      </Box>
    </Box>
  )
}

/**
 * A scale's events as a Gantt chart (see `ganttChart`): a row per stay and
 * BOMBEO and one for every MARPOL, in the list's Eventos order, each bar on one
 * time axis that fills the page's width, so overlaps between stays, MARPOL and
 * BOMBEO show at a glance and the page never scrolls sideways. Read in start
 * order, the circles are the list's Eventos.
 */
export const ScaleTimeline: FC<{ events: EventRow[]; now: number }> = ({ events, now }) => {
  const chart = ganttChart(events, now)
  if (chart === null) return null
  return (
    <Box
      data-testid="scale-timeline"
      border={1}
      borderColor="divider"
      borderRadius={1}
      px={1.5}
      py={1}
      sx={{ display: 'grid', gridTemplateColumns: COLUMNS, overflow: 'hidden' }}
    >
      <Axis chart={chart} />
      {chart.rows.map(bars => (
        <Box key={bars[0]?.marker.id} gridColumn="1 / -1">
          {bars[0]?.marker.type === 'MARPOL' ? <MarpolRow bars={bars} chart={chart} /> : bars[0] && <Row bar={bars[0]} chart={chart} />}
        </Box>
      ))}
    </Box>
  )
}
